import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { fetchAuthConfig, getLocalUser, logout } from "../lib/auth";
import { heldOrgs, heldRoles } from "../lib/permissions";
import type { Me } from "../types";

const initial = (s: string) => (s.trim().charAt(0) || "?").toUpperCase();

const ROLE_LABEL: Record<string, string> = { admin: "Admin", tech_writer: "Tech Writer", sme: "SME", reader: "Reader" };
const roleLabel = (r: string) => ROLE_LABEL[r] ?? r;

// Bottom-of-rail identity strip: who is signed in, with a sign-out control.
// Identity comes from the server (GET /api/me), so the box renders a real user
// in BOTH environments — the Okta-verified user when deployed, and the shared
// "Local User" off-Okta. Sign-out only appears when Okta is enabled (off-Okta
// there is no session to drop).
export default function UserFooter() {
  const [me, setMe] = useState<Me | null>(null);
  const [canSignOut, setCanSignOut] = useState(false);

  useEffect(() => {
    void api.getMe().then(setMe).catch(() => setMe(null));
    // Sign-out is meaningful when Okta is on (drop the token) or when a local
    // identity is set off-Okta (clear it → back to the Omni Hub sign-in).
    void fetchAuthConfig().then((c) => setCanSignOut(c.enabled || getLocalUser() !== null));
  }, []);

  // Prefer the display name; fall back to the email's local-part, then a label.
  const name = me?.name || me?.email?.split("@")[0] || "Signed out";
  const sub = me?.email ?? "not signed in";

  // A compact RBAC role chip — the one place every user (not just admins, who get
  // the full view under Settings → Account) sees the identity resolved for them.
  const role = me ? (me.admin ? "admin" : heldRoles(me)[0]) : null;
  const orgs = heldOrgs(me);
  const roleTitle = me
    ? me.admin
      ? "Omni Hub admin — all organizations"
      : `${heldRoles(me).map(roleLabel).join(", ")}${orgs.length ? ` · ${orgs.join(", ")}` : ""}`
    : undefined;

  return (
    <div className="user-footer" title={me?.email ?? "Not signed in"}>
      <span className="user-footer-ava">{initial(name)}</span>
      <span className="user-footer-main">
        <span className="user-footer-name">{name}</span>
        <span className="user-footer-sub">{sub}</span>
      </span>
      {role && (
        <span className={`user-footer-role dh-role-pill role-${role}`} title={roleTitle}>{roleLabel(role)}</span>
      )}
      {canSignOut && me && (
        <button className="user-footer-out" onClick={() => logout()} title="Sign out" aria-label="Sign out">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
        </button>
      )}
    </div>
  );
}
